import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import Icon from "../../components/AppIcon";
import ContractTypeSelector from "./components/ContractTypeSelector";
import ContractFormStepper from "./components/ContractFormStepper";
import DynamicContractForm from "./components/DynamicContractForm";
import PersonalizedContractForm from "./components/PersonalizedContractForm";
import DocumentFillerChatPanel from "./components/DocumentFillerChatPanel";
import ImportDocumentDataModal from "./components/ImportDocumentDataModal";
import LiveContractPreview from "./components/LiveContractPreview";
import PersonalizedLiveContractPreview from "./components/PersonalizedLiveContractPreview";
import GenerateContractButton from "./components/GenerateContractButton";
import contractService from "../../services/contractService";
import { listUserTemplates, getUserTemplateById } from "../../services/templateService";
import {
  CONTRACT_METHOD_PERSONALIZED,
  CONTRACT_METHOD_STANDARD,
  initializePersonalizedFormData,
  mergeSavedDataIntoPersonalizedForm,
  normalizeLoadedPersonalTemplate,
  validatePersonalizedForm,
  buildCustomTemplateContractPayload,
  isCustomTemplateFillingType,
} from "./utils/personalizedTemplate";
import { buildContractPayload } from "../../utils/buildContractPayload";
import { resolveApiErrorMessage } from "../../utils/apiErrorMessages";
import { trackDocumentJob } from "../../utils/documentJobTracker";
import { pollDocumentJobStatus } from "../../utils/pollDocumentJobStatus";
import {
  MAX_DOCUMENT_TITLE_LENGTH,
  clampDocumentTitle,
} from "../../utils/documentTitle";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import LoadingMark from "../../components/ui/LoadingMark";
import toast from "react-hot-toast";
import { ModalShell } from "../../components/ui/ModalFrame";
import { MODAL_LAYER } from "../../constants/modalLayers";
import userService from "services/userService";

import { documentDefinitions } from "../../documents";
import { isRepeatableFieldVisible } from "../../utils/repeatableFieldVisible";
import { isSchemaSectionShown } from "../../utils/schemaSectionVisibility";
import {
  normalizeCompromissoLoadedFormData,
  normalizeContratoLocacaoLoadedFormData,
  normalizeAutorizacaoLocacaoLoadedFormData,
  normalizeParceriaVendasLoadedFormData,
} from "../../utils/compromissoFormDataNormalize";
import { normalizeReciboSimplesLoadedFormData } from "../../utils/reciboSimplesFormDataNormalize";
import DemoModeBanner from "../../components/demo/DemoModeBanner";
import LicenseLimitBanner from "../../components/license/LicenseLimitBanner";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";
import DemoSignupGateModal from "../../components/demo/DemoSignupGateModal";
import {
  DEMO_INTENT,
  DEMO_TEMPLATE_IDS,
  POST_AUTH_INTENT_KEY,
} from "../../constants/demoContract";
import { savePendingDemoContract } from "../../utils/demoContractStorage";

const PROCESSING_PROGRESS_MESSAGES = [
  "Recebendo informacoes",
  "Validando seu documento",
  "Consultando Modelo",
  "Criando seu arquivo",
  "Convertendo para PDF",
  "Preparacoes Finais",
];

const toFrontendTemplateId = (backendTemplateKey) => {
  if (!backendTemplateKey || typeof backendTemplateKey !== "string") {
    return null;
  }

  const normalized = backendTemplateKey.toLowerCase().replace(/-/g, "_");

  const map = {
    compromisso_compra_venda: "compromisso-compra-venda",
    contrato_locacao_imovel: "contrato-locacao-imovel",
    autorizacao_locacao_imovel: "autorizacao-locacao-imovel",
    proposta_compra_imovel: "proposta-compra-imovel",
    contrato_parceria_vendas: "contrato-parceria-vendas",
    recibo_simples: "recibo-simples",
    autorizacao_gleba_loteamento: "autorizacao-gleba-loteamento",
    autorizacao_venda_imovel: "autorizacao-venda-imovel",
  };

  const fromMap = map[normalized];
  if (fromMap) return fromMap;

  return normalized.replace(/_/g, "-");
};

/** Query param for GET /get-documents-by-template (matches buildContractPayload templateKey). */
const toBackendTemplateKey = (frontendDocumentId) => {
  if (!frontendDocumentId || typeof frontendDocumentId !== "string") {
    return "";
  }
  return frontendDocumentId.replace(/-/g, "_").toLowerCase();
};

function pickNeoCoinsFromUser(user) {
  if (!user || typeof user !== "object") return null;
  const raw = user.neo_coins ?? user.neoCoins ?? user.neo_coin ?? user.neoCoin;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : String(raw);
}

/** Match conditional schema option keys case-insensitively (same as DynamicContractForm). */
function getConditionalOptionKey(section, value) {
  if (!value || !section.options) return value;
  const key = Object.keys(section.options).find(
    (k) => String(k).toLowerCase() === String(value).toLowerCase()
  );
  return key ?? value;
}

const mapBackendDataToFormData = (backendData) => {
  if (!backendData || typeof backendData !== "object") return {};

  const keyMap = {
    VENDEDORES: "vendedores",
    COMPRADORES: "compradores",
    LOCADORES: "locadores",
    LOCATARIOS: "locatarios",
    FIADORES: "fiadores",
    TESTEMUNHAS: "testemunhas",
    TIPO_IMOVEL: "tipo_imovel",
    CLAUSULA_GARANTIA: "clausula_garantia",
    TIPO_PAGAMENTO: "tipo_pagamento",
    TIPO_COMISSAO: "tipo_comissao",
    PROPRIETARIOS: "proprietarios",
    INTERMEDIADOR: "intermediador",
    CAPTANTES: "captantes",
    INTERMEDIADORAS: "intermediadores",
    INTERMEDIADORES: "intermediadores",
    cidade_assinatura: "CIDADE_ASSINATURA",
    data_assinatura: "DATA_ASSINATURA",
  };

  return Object.entries(backendData).reduce((acc, [key, value]) => {
    const mappedKey = keyMap[key] ?? key;
    acc[mappedKey] = value;
    return acc;
  }, {});
};

const ContractGenerator = ({ mode = "full" }) => {
  const isDemoMode = mode === "demo";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [selectedDocumentId, setSelectedDocumentId] =
    useState("compromisso-compra-venda");

  const [jobId, setJobId] = useState(null);

  const [documentTitle, setDocumentTitle] = useState("");
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [userConfiguration, setUserConfiguration] = useState(null);
  const [generatorUser, setGeneratorUser] = useState(null);
  const [neoCoins, setNeoCoins] = useState(null);

  const [generationStatus, setGenerationStatus] = useState("idle");
  const [scrollToPlaceholder, setScrollToPlaceholder] = useState(null);
  const [highlightedPlaceholder, setHighlightedPlaceholder] =
    useState(null);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [loadedStatus, setLoadedStatus] = useState(null);
  const [remainingEditions, setRemainingEditions] = useState(null);
  const [errorModalMessage, setErrorModalMessage] = useState(null);
  const [isLoadingDocumentForEdit, setIsLoadingDocumentForEdit] = useState(false);
  const [formStepIndex, setFormStepIndex] = useState(0);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [contractMethod, setContractMethod] = useState(CONTRACT_METHOD_STANDARD);
  const [userTemplates, setUserTemplates] = useState([]);
  const [userTemplatesLoading, setUserTemplatesLoading] = useState(false);
  const [userTemplateDetailLoading, setUserTemplateDetailLoading] = useState(false);
  const [selectedUserTemplateId, setSelectedUserTemplateId] = useState("");
  const [loadedPersonalTemplate, setLoadedPersonalTemplate] = useState(null);
  const isPersonalizedMode = contractMethod === CONTRACT_METHOD_PERSONALIZED;
  const isLocalEnv =
    import.meta.env.DEV &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [signupGateOpen, setSignupGateOpen] = useState(false);
  const desktopPreviewScrollRef = useRef(null);
  const mobilePreviewScrollRef = useRef(null);

  const definition = isPersonalizedMode
    ? null
    : documentDefinitions[selectedDocumentId];
  const documentFillerTemplateKey = isPersonalizedMode
    ? loadedPersonalTemplate?.templateKey
    : selectedDocumentId;
  const currentDocument = definition?.template;
  const currentFormFields = definition?.formSchema ?? [];
  const formStepsConfig = definition?.formSteps;
  const useFormWizard =
    Array.isArray(formStepsConfig) && formStepsConfig.length > 0;
  const scrollMapping = definition?.preview?.scrollMapping ?? {};

  const availableContractTypes = useMemo(() => {
    const allTypes = Object.values(documentDefinitions).map((def) => ({
      value: def.id,
      label: def.label,
    }));
    if (!isDemoMode) return allTypes;
    return allTypes.filter((item) => DEMO_TEMPLATE_IDS.includes(item.value));
  }, [isDemoMode]);

  const wizardFormFields = useMemo(() => {
    if (!useFormWizard) return currentFormFields;
    if (formStepIndex === 0) return [];
    const step = formStepsConfig[formStepIndex];
    if (step?.review) return currentFormFields;
    if (!step?.sectionIds?.length) return [];
    const idSet = new Set(step.sectionIds);
    return currentFormFields.filter((s) => idSet.has(s.sectionId));
  }, [useFormWizard, formStepIndex, formStepsConfig, currentFormFields]);

  const isReviewStep =
    useFormWizard &&
    Boolean(formStepsConfig?.[formStepIndex]?.review);

  if (!isPersonalizedMode && !definition) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 py-16">
        <p className="text-muted-foreground text-center">
          Tipo de documento não encontrado.
        </p>
      </div>
    );
  }

  /* Initialize formData */
  const initializeFormData = (schema) => {
    const data = {};

    schema.forEach((section) => {
      if (section.repeatable) {
        data[section.groupKey] = [];
      } else if (section.type === "conditional") {
        data[section.groupKey] = null;
      } else if (Array.isArray(section.fields)) {
        section.fields.forEach((field) => {
          if (field.default != null && field.id) {
            data[field.id] = field.default;
          }
        });
      }
    });

    return data;
  };

  /* Load user config */
  useEffect(() => {
    if (isDemoMode) return undefined;

    const loadConfig = async () => {
      const result = await userService.loadUserConfigurations();
      if (result?.configuration) {
        setUserConfiguration(result.configuration);
      }
    };

    loadConfig();
  }, [isDemoMode]);

  useEffect(() => {
    if (isDemoMode) return undefined;

    let cancelled = false;
    (async () => {
      const result = await userService.getUserInfo();
      if (cancelled) return;
      if (result?.success && result.data) {
        setGeneratorUser(result.data);
        setNeoCoins(pickNeoCoinsFromUser(result.data));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isDemoMode]);

  /* Map user config keys (snake_case) to form placeholder ids (UPPER_CASE) for seeding */
  const configKeyToFormId = {
    creci_imobiliaria: "CRECI_IMOBILIARIA",
    imobiliaria: "IMOBILIARIA",
    nome_imobiliaria: "IMOBILIARIA",
  };

  /* Seed form with user configuration for scalar fields (so inputs show config values) */
  useEffect(() => {
    if (isPersonalizedMode) return;
    if (!userConfiguration || !currentFormFields) return;
    if (isEditingExisting) return;

    const scalarFieldIds = new Set();
    currentFormFields.forEach((section) => {
      if (!section.repeatable && section.type !== "conditional" && section.fields) {
        section.fields.forEach((f) => scalarFieldIds.add(f.id));
      }
    });

    const seeded = {};
    Object.entries(configKeyToFormId).forEach(([configKey, formId]) => {
      if (!scalarFieldIds.has(formId)) return;
      const value = userConfiguration[configKey] ?? userConfiguration[configKey.replace(/_/g, "")];
      if (value === undefined || value === null || String(value).trim() === "") return;
      seeded[formId] = value;
    });

    if (Object.keys(seeded).length === 0) return;

    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(seeded).forEach(([id, value]) => {
        const current = prev[id];
        if (current === undefined || current === null || current === "") {
          next[id] = value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [userConfiguration, selectedDocumentId, currentFormFields, isEditingExisting, isPersonalizedMode]);

  useEffect(() => {
    if (isPersonalizedMode) return;
    setFormStepIndex(0);
  }, [selectedDocumentId, isPersonalizedMode]);

  useEffect(() => {
    if (!useFormWizard) return;
    const el = document.getElementById("contract-generator-form-scroll");
    if (el) el.scrollTop = 0;
  }, [formStepIndex, useFormWizard]);

  /* Reset form when template changes; seed with user config when available */
  useEffect(() => {
    if (isPersonalizedMode) return;
    if (!currentFormFields) return;
    if (isEditingExisting) return;
    const base = initializeFormData(currentFormFields);
    if (userConfiguration) {
      const scalarFieldIds = new Set();
      currentFormFields.forEach((section) => {
        if (!section.repeatable && section.type !== "conditional" && section.fields) {
          section.fields.forEach((f) => scalarFieldIds.add(f.id));
        }
      });
      Object.entries(configKeyToFormId).forEach(([configKey, formId]) => {
        if (!scalarFieldIds.has(formId)) return;
        const value = userConfiguration[configKey] ?? userConfiguration[configKey.replace(/_/g, "")];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          base[formId] = value;
        }
      });
    }
    setFormData(base);
    setErrors({});
  }, [selectedDocumentId, isEditingExisting, currentFormFields, userConfiguration, isPersonalizedMode]);

  /* Load existing document when job_id is present */
  useEffect(() => {
    if (isDemoMode) return undefined;

    const jobIdFromQuery = searchParams.get("job_id");
    if (!jobIdFromQuery) return;

    const loadDocument = async () => {
      setIsLoadingDocumentForEdit(true);
      try {
        const result = await contractService.getDocumentByJobId(jobIdFromQuery);

        if (!result?.success) {
          showToast("error", "Documento nao encontrado!");
          return;
        }

        const doc = result.data || {};
        if (!doc || Object.keys(doc).length === 0) {
          showToast("error", "Documento nao encontrado!");
          return;
        }

        setIsEditingExisting(true);

        if (isCustomTemplateFillingType(doc)) {
          setContractMethod(CONTRACT_METHOD_PERSONALIZED);

          const templateId = doc.template_id ?? doc.templateId;
          if (!templateId) {
            showToast("error", "Documento sem modelo associado.");
            return;
          }

          setSelectedUserTemplateId(String(templateId));

          const templateResult = await getUserTemplateById(templateId);
          if (!templateResult.success) {
            showToast(
              "error",
              templateResult.error || "Erro ao carregar modelo do documento."
            );
            return;
          }

          const loaded = normalizeLoadedPersonalTemplate(templateResult.data);
          setLoadedPersonalTemplate(loaded);

          if (doc.title) {
            setDocumentTitle(clampDocumentTitle(doc.title));
          }

          if (doc.job_id) {
            setJobId(doc.job_id);
          }

          if (doc.status) {
            setLoadedStatus(doc.status);
          }

          if (typeof doc.remaining_editions === "number") {
            setRemainingEditions(doc.remaining_editions);
          }

          const savedData =
            doc.data && typeof doc.data === "object" ? doc.data : {};
          setFormData(
            mergeSavedDataIntoPersonalizedForm(
              loaded?.formFields ?? [],
              savedData
            )
          );
          return;
        }

        const frontendTemplateId = toFrontendTemplateId(
          doc.templateKey || doc.template_key
        );
        if (frontendTemplateId && documentDefinitions[frontendTemplateId]) {
          setSelectedDocumentId(frontendTemplateId);
        }

        if (doc.title) {
          setDocumentTitle(clampDocumentTitle(doc.title));
        }

        if (doc.job_id) {
          setJobId(doc.job_id);
        }

        if (doc.status) {
          setLoadedStatus(doc.status);
        }

        if (typeof doc.remaining_editions === "number") {
          setRemainingEditions(doc.remaining_editions);
        }

        if (doc.data && typeof doc.data === "object") {
          const schemaForInit =
            frontendTemplateId && documentDefinitions[frontendTemplateId]
              ? documentDefinitions[frontendTemplateId].formSchema
              : [];

          let mapped = mapBackendDataToFormData(doc.data);
          if (
            frontendTemplateId === "compromisso-compra-venda" ||
            frontendTemplateId === "proposta-compra-imovel"
          ) {
            mapped = normalizeCompromissoLoadedFormData(mapped);
          } else if (frontendTemplateId === "contrato-parceria-vendas") {
            mapped = normalizeParceriaVendasLoadedFormData(mapped);
          } else if (frontendTemplateId === "contrato-locacao-imovel") {
            mapped = normalizeContratoLocacaoLoadedFormData(mapped);
          } else if (frontendTemplateId === "autorizacao-locacao-imovel") {
            mapped = normalizeAutorizacaoLocacaoLoadedFormData(mapped);
          } else if (frontendTemplateId === "autorizacao-gleba-loteamento") {
            mapped = normalizeAutorizacaoLocacaoLoadedFormData(mapped);
          } else if (frontendTemplateId === "autorizacao-venda-imovel") {
            mapped = normalizeAutorizacaoLocacaoLoadedFormData(mapped);
          } else if (frontendTemplateId === "recibo-simples") {
            mapped = normalizeReciboSimplesLoadedFormData(mapped);
          }

          setFormData({
            ...initializeFormData(schemaForInit),
            ...mapped,
          });
        }
      } catch (_err) {
        showToast("error", "Erro ao carregar documento para edicao.");
      } finally {
        setIsLoadingDocumentForEdit(false);
      }
    };

    loadDocument();
  }, [searchParams, isDemoMode]);

  /* Bootstrap personalized mode from user-templates list (user_template_id query param) */
  useEffect(() => {
    if (isDemoMode) return undefined;
    if (searchParams.get("job_id")) return;

    const userTemplateIdFromQuery = searchParams.get("user_template_id");
    if (!userTemplateIdFromQuery) return;

    const loadFromUserTemplate = async () => {
      setIsLoadingDocumentForEdit(true);
      try {
        setContractMethod(CONTRACT_METHOD_PERSONALIZED);

        const [templatesResult, templateResult] = await Promise.all([
          listUserTemplates(),
          getUserTemplateById(userTemplateIdFromQuery),
        ]);

        if (templatesResult.success) {
          setUserTemplates(templatesResult.data?.items ?? []);
        } else {
          setUserTemplates([]);
        }

        if (!templateResult.success) {
          showToast(
            "error",
            templateResult.error || "Erro ao carregar modelo."
          );
          return;
        }

        const loaded = normalizeLoadedPersonalTemplate(templateResult.data);
        setSelectedUserTemplateId(String(userTemplateIdFromQuery));
        setLoadedPersonalTemplate(loaded);
        setFormData(initializePersonalizedFormData(loaded?.formFields ?? []));
        setDocumentTitle(clampDocumentTitle(loaded?.name ?? ""));
        navigate("/contract-generator", { replace: true });
      } catch (_err) {
        showToast("error", "Erro ao carregar modelo.");
      } finally {
        setUserTemplatesLoading(false);
        setIsLoadingDocumentForEdit(false);
      }
    };

    loadFromUserTemplate();
  }, [searchParams, navigate]);

  /* Bootstrap standard mode from model library (document_id query param) */
  useEffect(() => {
    if (isDemoMode) return undefined;
    if (searchParams.get("job_id")) return;
    if (searchParams.get("user_template_id")) return;

    const documentIdFromQuery = searchParams.get("document_id");
    if (!documentIdFromQuery) return;

    const definitionFromQuery = documentDefinitions[documentIdFromQuery];
    if (!definitionFromQuery) return;

    setContractMethod(CONTRACT_METHOD_STANDARD);
    setSelectedDocumentId(documentIdFromQuery);
    setSelectedUserTemplateId("");
    setLoadedPersonalTemplate(null);
    setFormData(initializeFormData(definitionFromQuery.formSchema ?? []));
    setDocumentTitle("");
    setFormStepIndex(0);
    setIsEditingExisting(false);
    navigate("/contract-generator", { replace: true });
  }, [searchParams, navigate, isDemoMode]);

  /* Lock scroll when preview open */
  useEffect(() => {
    if (mobilePreviewOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [mobilePreviewOpen]);

  /* Toast helper */
  const showToast = useCallback((type, message, duration = 3000) => {
    if (type === "success") {
      toast.success(message, { duration });
    } else if (type === "error") {
      toast.error(message, { duration });
    } else {
      toast(message, { duration });
    }
  }, []);

  useEffect(() => {
    if (!location.state?.demoRestored || isDemoMode) return;
    showToast("success", "Seu documento de demonstração foi salvo como rascunho.");
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {},
    });
  }, [location.pathname, location.search, location.state, isDemoMode, navigate, showToast]);

  /* Load user templates for personalized mode */
  useEffect(() => {
    if (!isPersonalizedMode || isEditingExisting) return undefined;

    let cancelled = false;

    const loadTemplates = async () => {
      setUserTemplatesLoading(true);
      const result = await listUserTemplates();
      if (cancelled) return;

      if (result.success) {
        setUserTemplates(result.data.items ?? []);
      } else {
        setUserTemplates([]);
        showToast("error", result.error || "Erro ao carregar modelos.");
      }
      setUserTemplatesLoading(false);
    };

    loadTemplates();

    return () => {
      cancelled = true;
    };
  }, [isPersonalizedMode, isEditingExisting]);

  const normalizeContractErrorMessage = (errorValue) => {
    if (!errorValue) return "Erro ao gerar documento.";
    return resolveApiErrorMessage(errorValue);
  };

  /* Dirty detection */
  const hasDirtyFields = useMemo(() => {
    return (
      documentTitle.trim() !== "" ||
      Object.values(formData).some((v) => {
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "object" && v !== null)
          return v.type || Object.keys(v.fields || {}).length > 0;
        return v !== null && v !== undefined && v !== "";
      })
    );
  }, [formData, documentTitle]);

  useUnsavedChangesGuard(
    !isDemoMode &&
      generationStatus === "idle" &&
      !isLoadingDocumentForEdit &&
      hasDirtyFields
  );

  /* Scroll preview sync */
  const handleFieldFocus = useCallback(
    (key) => {
      const placeholder = isPersonalizedMode ? key : scrollMapping[key] ?? key;
      setScrollToPlaceholder(placeholder);
      setHighlightedPlaceholder(placeholder);
    },
    [scrollMapping, isPersonalizedMode]
  );

  const handleFieldBlur = useCallback(() => {
    setHighlightedPlaceholder(null);
  }, []);

  const handleAppendRepeatableRows = useCallback(
    (groupKey, rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return;

      const section = currentFormFields?.find(
        (s) => s.repeatable && s.groupKey === groupKey
      );
      const maxItems = section?.maxItems;

      setFormData((prev) => {
        let nextRows;
        if (maxItems === 1) {
          nextRows = [rows[0]];
        } else {
          nextRows = [...(prev[groupKey] || []), ...rows];
          if (maxItems != null) {
            nextRows = nextRows.slice(0, maxItems);
          }
        }
        return { ...prev, [groupKey]: nextRows };
      });

      handleFieldFocus(groupKey);
      const appliedCount =
        maxItems === 1 ? 1 : Math.min(rows.length, maxItems ?? rows.length);
      showToast(
        "success",
        appliedCount === 1
          ? "1 cliente adicionado ao formulário."
          : `${appliedCount} clientes adicionados ao formulário.`
      );
    },
    [currentFormFields, handleFieldFocus, showToast]
  );

  const handleImportDocumentData = useCallback(
    (doc) => {
      if (!doc?.data || typeof doc.data !== "object") {
        showToast("error", "Documento sem dados para importar.");
        return;
      }

      let mapped = mapBackendDataToFormData(doc.data);
      if (
        selectedDocumentId === "compromisso-compra-venda" ||
        selectedDocumentId === "proposta-compra-imovel"
      ) {
        mapped = normalizeCompromissoLoadedFormData(mapped);
      } else if (selectedDocumentId === "contrato-parceria-vendas") {
        mapped = normalizeParceriaVendasLoadedFormData(mapped);
      } else if (selectedDocumentId === "contrato-locacao-imovel") {
        mapped = normalizeContratoLocacaoLoadedFormData(mapped);
      } else if (selectedDocumentId === "autorizacao-locacao-imovel") {
        mapped = normalizeAutorizacaoLocacaoLoadedFormData(mapped);
      } else if (selectedDocumentId === "autorizacao-gleba-loteamento") {
        mapped = normalizeAutorizacaoLocacaoLoadedFormData(mapped);
      } else if (selectedDocumentId === "autorizacao-venda-imovel") {
        mapped = normalizeAutorizacaoLocacaoLoadedFormData(mapped);
      } else if (selectedDocumentId === "recibo-simples") {
        mapped = normalizeReciboSimplesLoadedFormData(mapped);
      }

      setFormData({
        ...initializeFormData(currentFormFields),
        ...mapped,
      });

      const t = doc.title;
      if (typeof t === "string" && t.trim()) {
        setDocumentTitle(clampDocumentTitle(t.trim()));
      }

      setJobId(null);
      setIsEditingExisting(false);
      setLoadedStatus(null);
      setRemainingEditions(null);
      setErrors({});
      navigate("/contract-generator", { replace: true });
      showToast("success", "Dados importados com sucesso.");
    },
    [currentFormFields, navigate, selectedDocumentId]
  );

  const handleOpenImportModal = useCallback(() => {
    setImportModalOpen(true);
  }, []);

  const handleFillContratoLocacaoTestData = useCallback(() => {
    if (selectedDocumentId !== "contrato-locacao-imovel") return;

    const pessoaFisicaBase = (nome, cpf, email) => ({
      NOME_PESSOA_FISICA: nome,
      CPF_PESSOA_FISICA: cpf,
      RG_PESSOA_FISICA: "1234567 SSP/SP",
      NACIONALIDADE_PESSOA_FISICA: "Brasileira",
      PROFISSAO_PESSOA_FISICA: "Empresario",
      ESTADO_CIVIL_PESSOA_FISICA: "casado",
      NOME_CONJUGE: `${nome} Conjuge`,
      CPF_CONJUGE: "987.654.321-00",
      RG_CONJUGE: "7654321 SSP/SP",
      NACIONALIDADE_CONJUGE: "Brasileira",
      PROFISSAO_CONJUGE: "Advogada",
      EMAIL_CONJUGE: "conjuge.teste@exemplo.com",
      RUA_ENDERECO_PESSOA_FISICA: "Rua das Palmeiras",
      N_ENDERECO_PESSOA_FISICA: "123",
      BAIRRO_ENDERECO_PESSOA_FISICA: "Centro",
      MUN_ENDERECO_PESSOA_FISICA: "Sao Paulo",
      CEP_PESSOA_FISICA: "01001-000",
      EMAIL_PESSOA_FISICA: email,
      TEL_PESSOA_FISICA: "(11) 99999-9999",
    });

    setDocumentTitle("Contrato de Locacao - Teste");
    setFormData({
      ...initializeFormData(currentFormFields),
      locadores: [
        pessoaFisicaBase(
          "Joao Locador",
          "111.222.333-44",
          "locador.teste@exemplo.com"
        ),
      ],
      locatarios: [
        pessoaFisicaBase(
          "Maria Locataria",
          "555.666.777-88",
          "locataria.teste@exemplo.com"
        ),
      ],
      testemunhas: [
        {
          NOME_TESTEMUNHA: "Ana Testemunha",
          CPF_TESTEMUNHA: "123.123.123-12",
          EMAIL_TESTEMUNHA: "testemunha1.teste@exemplo.com",
        },
        {
          NOME_TESTEMUNHA: "Pedro Testemunha",
          CPF_TESTEMUNHA: "321.321.321-21",
          EMAIL_TESTEMUNHA: "testemunha2.teste@exemplo.com",
        },
      ],
      tipo_imovel: {
        type: "casa",
        fields: {
          ENDERECO_IMOVEL: "Rua das Acacias",
          NUMERO_IMOVEL: "456",
          BAIRRO_IMOVEL: "Jardins",
          MUN_IMOVEL: "Sao Paulo",
          AREA_CONSTRUIDA: "150",
          AREA_TERRENO: "220",
          DESCRICAO_IMOVEL: "Casa residencial com 3 dormitorios",
          MAT_IMOVEL: "12345",
          N_CARTORIO: "2o Cartorio de Registro de Imoveis",
          MUN_CARTORIO: "Sao Paulo",
        },
      },
      USO_LOCACAO: "residencial",
      clausula_garantia: {
        type: "caucao",
        fields: {
          VALOR_CAUCAO: "6000",
          MESES_CAUCAO: 3,
        },
      },
      VALOR_ALUGUEL: "2000",
      DATA_PAGAMENTO: "05",
      MULTA_ATRASO: "2,00",
      MULTA_INFRACAO: "3,00",
      MULTA_RESCISAO_ANTECIPADA: "4000",
      PRAZO_LOCACAO: 30,
      DIAS_DESOCUPAR: 30,
      CIDADE_ASSINATURA: "Sao Paulo",
      DATA_ASSINATURA: "2026-04-28",
    });
    setErrors({});
    showToast("success", "Formulario preenchido com dados de teste.");
  }, [currentFormFields, selectedDocumentId]);

  const handleFillAutorizacaoLocacaoTestData = useCallback(() => {
    if (selectedDocumentId !== "autorizacao-locacao-imovel") return;

    const pessoaFisicaBase = (nome, cpf, email) => ({
      NOME_PESSOA_FISICA: nome,
      CPF_PESSOA_FISICA: cpf,
      RG_PESSOA_FISICA: "1234567 SSP/SP",
      NACIONALIDADE_PESSOA_FISICA: "Brasileira",
      PROFISSAO_PESSOA_FISICA: "Empresario",
      ESTADO_CIVIL_PESSOA_FISICA: "casado",
      NOME_CONJUGE: `${nome} Conjuge`,
      CPF_CONJUGE: "987.654.321-00",
      RG_CONJUGE: "7654321 SSP/SP",
      NACIONALIDADE_CONJUGE: "Brasileira",
      PROFISSAO_CONJUGE: "Advogada",
      EMAIL_CONJUGE: "conjuge.teste@exemplo.com",
      RUA_ENDERECO_PESSOA_FISICA: "Rua das Palmeiras",
      N_ENDERECO_PESSOA_FISICA: "123",
      BAIRRO_ENDERECO_PESSOA_FISICA: "Centro",
      MUN_ENDERECO_PESSOA_FISICA: "Sao Paulo",
      CEP_PESSOA_FISICA: "01001-000",
      EMAIL_PESSOA_FISICA: email,
      TEL_PESSOA_FISICA: "(11) 99999-9999",
    });

    setDocumentTitle("Autorizacao Locacao - Teste");
    setFormData({
      ...initializeFormData(currentFormFields),
      proprietarios: [
        pessoaFisicaBase(
          "Carlos Proprietario",
          "111.222.333-44",
          "proprietario.teste@exemplo.com"
        ),
      ],
      intermediador: {
        type: "corretor",
        fields: {
          NOME_CORRETOR: "Paulo Corretor",
          NACIONALIDADE_CORRETOR: "Brasileira",
          CPF_CORRETOR: "123.456.789-00",
          RG_CORRETOR: "2098756 SSP/SP",
          CRECI_CORRETOR: "98765-F",
          RUA_ENDERECO_CORRETOR: "Rua Alpha",
          N_ENDERECO_CORRETOR: "500",
          BAIRRO_ENDERECO_CORRETOR: "Centro",
          MUN_ENDERECO_CORRETOR: "Campinas",
          CEP_CORRETOR: "13010-000",
          EMAIL_CORRETOR: "corretor.teste@exemplo.com",
          TEL_CORRETOR: "(19) 97777-6666",
        },
      },
      testemunhas: [
        {
          NOME_TESTEMUNHA: "Ana Testemunha",
          CPF_TESTEMUNHA: "123.123.123-12",
          EMAIL_TESTEMUNHA: "testemunha1.teste@exemplo.com",
        },
        {
          NOME_TESTEMUNHA: "Pedro Testemunha",
          CPF_TESTEMUNHA: "321.321.321-21",
          EMAIL_TESTEMUNHA: "testemunha2.teste@exemplo.com",
        },
      ],
      tipo_imovel: {
        type: "casa",
        fields: {
          ENDERECO_IMOVEL: "Rua das Acacias",
          NUMERO_IMOVEL: "456",
          BAIRRO_IMOVEL: "Jardins",
          MUN_IMOVEL: "Sao Paulo",
          AREA_CONSTRUIDA: "150",
          AREA_TERRENO: "220",
          DESCRICAO_IMOVEL: "Casa residencial com 3 dormitorios",
          MAT_IMOVEL: "12345",
          N_CARTORIO: "2o Cartorio de Registro de Imoveis",
          MUN_CARTORIO: "Sao Paulo",
        },
      },
      EXCLUSIVIDADE: "exclusividade",
      PRAZO_AUTORIZACAO: 12,
      PERCENTUAL_COMISSAO_CAPTACAO: "5,00",
      PERCENTUAL_ADMINISTRACAO: "10,00",
      CIDADE_ASSINATURA: "Sao Paulo",
      DATA_ASSINATURA: "2026-05-04",
    });
    setErrors({});
    showToast("success", "Formulario preenchido com dados de teste.");
  }, [currentFormFields, selectedDocumentId]);

  const handleFillParceriaVendasTestData = useCallback(() => {
    if (selectedDocumentId !== "contrato-parceria-vendas") return;

    const corretorBase = (nome, cpf, email) => ({
      NOME_CORRETOR: nome,
      NACIONALIDADE_CORRETOR: "Brasileira",
      CPF_CORRETOR: cpf,
      RG_CORRETOR: "1234567 SSP/SP",
      CRECI_CORRETOR: "12345-F",
      RUA_ENDERECO_CORRETOR: "Rua das Palmeiras",
      N_ENDERECO_CORRETOR: "123",
      BAIRRO_ENDERECO_CORRETOR: "Centro",
      MUN_ENDERECO_CORRETOR: "Sao Paulo",
      CEP_CORRETOR: "01001-000",
      EMAIL_CORRETOR: email,
      TEL_CORRETOR: "(11) 99999-9999",
    });

    setDocumentTitle("Contrato Parceria Vendas - Teste");
    setFormData({
      ...initializeFormData(currentFormFields),
      captantes: [
        corretorBase(
          "Carla Captante",
          "111.222.333-44",
          "captante.teste@exemplo.com"
        ),
      ],
      intermediadores: [
        corretorBase(
          "Paulo Intermediador",
          "555.666.777-88",
          "intermediador.teste@exemplo.com"
        ),
      ],
      testemunhas: [
        {
          NOME_TESTEMUNHA: "Ana Testemunha",
          CPF_TESTEMUNHA: "123.123.123-12",
          EMAIL_TESTEMUNHA: "testemunha1.teste@exemplo.com",
        },
        {
          NOME_TESTEMUNHA: "Pedro Testemunha",
          CPF_TESTEMUNHA: "321.321.321-21",
          EMAIL_TESTEMUNHA: "testemunha2.teste@exemplo.com",
        },
      ],
      DESCRICAO_PRODUTO: "Consultoria imobiliaria e captacao de clientes",
      COMISSAO_INTERMEDIADOR: "6,50",
      MULTA_CONTRATUAL: "R$ 2.000,00 (dois mil reais)",
      FORO: "Sao Paulo/SP",
      CIDADE_ASSINATURA: "Sao Paulo",
      DATA_ASSINATURA: "2026-05-06",
    });
    setErrors({});
    showToast("success", "Formulario preenchido com dados de teste.");
  }, [currentFormFields, selectedDocumentId]);

  const userTemplateOptions = useMemo(
    () =>
      userTemplates.map((template) => ({
        value: template.id,
        label: template.name,
      })),
    [userTemplates]
  );

  const resetGeneratorSession = useCallback(() => {
    setDocumentTitle("");
    setFormData({});
    setErrors({});
    setJobId(null);
    setIsEditingExisting(false);
    setLoadedStatus(null);
    setRemainingEditions(null);
    setFormStepIndex(0);
    setSelectedUserTemplateId("");
    setLoadedPersonalTemplate(null);
    setScrollToPlaceholder(null);
    setHighlightedPlaceholder(null);
  }, []);

  const handleContractMethodChange = (method) => {
    if (method === contractMethod) return;

    if (hasDirtyFields) {
      const confirmed = window.confirm(
        "Ao trocar o método, todas as informações preenchidas serão perdidas. Deseja continuar?"
      );
      if (!confirmed) return;
    }

    resetGeneratorSession();
    setContractMethod(method);
    if (method === CONTRACT_METHOD_STANDARD) {
      setSelectedDocumentId("compromisso-compra-venda");
    }
  };

  const handleUserTemplateChange = async (templateId) => {
    if (!templateId || templateId === selectedUserTemplateId) return;

    if (hasDirtyFields && selectedUserTemplateId) {
      const confirmed = window.confirm(
        "Ao trocar o modelo, todas as informações preenchidas serão perdidas. Deseja continuar?"
      );
      if (!confirmed) return;
    }

    setSelectedUserTemplateId(templateId);
    setUserTemplateDetailLoading(true);
    setErrors({});

    try {
      const result = await getUserTemplateById(templateId);
      if (!result.success) {
        showToast("error", result.error || "Erro ao carregar modelo.");
        setLoadedPersonalTemplate(null);
        setFormData({});
        return;
      }

      const loaded = normalizeLoadedPersonalTemplate(result.data);
      setLoadedPersonalTemplate(loaded);
      setFormData(initializePersonalizedFormData(loaded?.formFields ?? []));
      setDocumentTitle(clampDocumentTitle(loaded?.name ?? ""));
    } catch (_err) {
      showToast("error", "Erro ao carregar modelo.");
      setLoadedPersonalTemplate(null);
      setFormData({});
    } finally {
      setUserTemplateDetailLoading(false);
    }
  };

  /* Change document */
  const handleDocumentChange = (newId) => {
    if (newId === selectedDocumentId) return;

    if (hasDirtyFields) {
      const confirmed = window.confirm(
        "Ao trocar o tipo de documento, todas as informações preenchidas serão perdidas. Deseja continuar?"
      );
      if (!confirmed) return;
    }

    setSelectedDocumentId(newId);
    setDocumentTitle("");
    setJobId(null);
    setIsEditingExisting(false);
    setLoadedStatus(null);
    setRemainingEditions(null);
  };

  const standardFormValidation = useMemo(() => {
    if (isPersonalizedMode) {
      return {
        requiredFieldsCount: 0,
        filledRequiredFields: 0,
        isFormValid: false,
      };
    }

    let requiredCount = 0;
    let filledCount = 0;

    requiredCount += 1;
    if (documentTitle.trim()) filledCount += 1;

    currentFormFields.forEach((section) => {
        if (section.repeatable) {
          if (!isSchemaSectionShown(section, formData)) {
            return;
          }

          const items = formData[section.groupKey] || [];

          const min = section.minItems ?? 0;
          if (min > 0) {
            requiredCount += 1;
            if (items.length >= min) filledCount += 1;
          }

          items.forEach((item) => {
            section.fields.forEach((field) => {
              if (
                field.required &&
                isRepeatableFieldVisible(field, item)
              ) {
                requiredCount += 1;
                const value = item[field.id];
                if (value !== null && value !== undefined && value !== "") {
                  filledCount += 1;
                }
              }
            });
          });

          return;
        }

        if (section.type === "conditional") {
          const groupValue = formData[section.groupKey];
          const selectedType = groupValue?.type;
          const optionsKey = selectedType
            ? getConditionalOptionKey(section, selectedType)
            : null;

          if (section.requireTypeSelection) {
            requiredCount += 1;
            if (optionsKey && section.options?.[optionsKey]) {
              filledCount += 1;
            }
          }

          if (selectedType && optionsKey && section.options?.[optionsKey]) {
            section.options[optionsKey].forEach((field) => {
              if (field.required) {
                requiredCount += 1;
                const value = groupValue.fields?.[field.id];
                if (value !== null && value !== undefined && value !== "") {
                  filledCount += 1;
                }
              }
            });
          }

          return;
        }

        section.fields?.forEach((field) => {
          if (field.required) {
            requiredCount += 1;
            const value = formData[field.id];
            if (value !== null && value !== undefined && value !== "") {
              filledCount += 1;
            }
          }
        });
      });

      const isValid =
        requiredCount > 0 && filledCount === requiredCount;

      return {
        requiredFieldsCount: requiredCount,
        filledRequiredFields: filledCount,
        isFormValid: isValid,
      };
    }, [currentFormFields, formData, documentTitle, isPersonalizedMode]);

  const personalizedFormValidation = useMemo(() => {
    if (!isPersonalizedMode) {
      return {
        requiredFieldsCount: 0,
        filledRequiredFields: 0,
        isFormValid: false,
      };
    }

    return validatePersonalizedForm({
      documentTitle,
      selectedUserTemplateId,
      templateKey: loadedPersonalTemplate?.templateKey,
      formFields: loadedPersonalTemplate?.formFields ?? [],
      formData,
    });
  }, [
    isPersonalizedMode,
    documentTitle,
    selectedUserTemplateId,
    loadedPersonalTemplate,
    formData,
  ]);

  const { requiredFieldsCount, filledRequiredFields, isFormValid } =
    isPersonalizedMode ? personalizedFormValidation : standardFormValidation;

  useEffect(() => {
    if (generationStatus !== "processing") {
      setProcessingMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setProcessingMessageIndex((prev) =>
        Math.min(prev + 1, PROCESSING_PROGRESS_MESSAGES.length - 1)
      );
    }, 900);

    return () => clearInterval(interval);
  }, [generationStatus]);

  /* Poll job status via existing GET /documents/{jobId} */
  useEffect(() => {
    if (generationStatus !== "processing" || !jobId) return undefined;

    trackDocumentJob({
      jobId,
      title: documentTitle || "Documento",
      kind: "pdf_generation",
    });

    const controller = new AbortController();

    (async () => {
      const outcome = await pollDocumentJobStatus(jobId, {
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      if (outcome.ok) {
        toast.success("PDF pronto! Redirecionando ao painel.");
        setGenerationStatus("idle");
        navigate("/contract-dashboard");
        return;
      }

      if (outcome.reason === "timeout") {
        toast("O PDF ainda está sendo processado. Acompanhe no painel de documentos.");
      }

      setGenerationStatus("idle");
      navigate("/contract-dashboard");
    })();

    return () => controller.abort();
  }, [generationStatus, jobId, documentTitle, navigate]);

  /* Save draft — returns true on success */
  const handleSaveDraft = async () => {
    if (generationStatus !== "idle") return false;

    setGenerationStatus("saving");

    let id = jobId;
    if (!id) {
      id = uuidv4();
      setJobId(id);
    }

    let payload;

    try {
      if (isPersonalizedMode) {
        payload = buildCustomTemplateContractPayload({
          templateKey: loadedPersonalTemplate?.templateKey,
          templateId: selectedUserTemplateId,
          title: documentTitle,
          formData,
          formFields: loadedPersonalTemplate?.formFields ?? [],
        });
      } else {
        payload = buildContractPayload({
          templateKey: selectedDocumentId,
          title: documentTitle,
          formData,
          fillingType: "standard",
          schema: currentFormFields,
        });
      }
    } catch (err) {
      showToast("error", err?.message || "Não foi possível montar o rascunho.");
      setGenerationStatus("idle");
      return false;
    }

    payload.job_id = id;

    try {
      const result = await contractService.generateDraft(payload);
      if (result?.success) {
        showToast("success", "Rascunho salvo com sucesso.");
        return true;
      }
      showToast(
        "error",
        normalizeContractErrorMessage(result?.error || "Erro ao salvar rascunho.")
      );
      return false;
    } catch (_err) {
      showToast("error", "Erro ao salvar rascunho.");
      return false;
    } finally {
      setGenerationStatus("idle");
    }
  };

  const handleBackToTemplate = async () => {
    if (!selectedUserTemplateId) return;
    const saved = await handleSaveDraft();
    if (!saved) return;
    navigate(
      `/edit-template?template_id=${encodeURIComponent(String(selectedUserTemplateId))}`
    );
  };

  /* Demo mode — gate PDF generation behind signup */
  const handleDemoGenerateGate = () => {
    if (generationStatus !== "idle" || !isFormValid) return;
    setSignupGateOpen(true);
  };

  const handleContinueToSignup = () => {
    const pendingJobId = jobId || uuidv4();
    if (!jobId) {
      setJobId(pendingJobId);
    }

    savePendingDemoContract({
      templateKey: selectedDocumentId,
      documentTitle,
      formData,
      jobId: pendingJobId,
    });
    sessionStorage.setItem(POST_AUTH_INTENT_KEY, DEMO_INTENT);
    setSignupGateOpen(false);
    navigate("/login", {
      state: {
        fromDemo: true,
        signupHint:
          "Crie sua conta para gerar o PDF e salvar seu documento como rascunho.",
      },
    });
  };

  /* Generate contract */
  const handleGenerateContract = async () => {
    if (generationStatus !== "idle") return;

    setGenerationStatus("generating");

    let id = jobId;
    if (!id) {
      id = uuidv4();
      setJobId(id);
    }

    let payload;

    try {
      if (isPersonalizedMode) {
        payload = buildCustomTemplateContractPayload({
          templateKey: loadedPersonalTemplate?.templateKey,
          templateId: selectedUserTemplateId,
          title: documentTitle,
          formData,
          formFields: loadedPersonalTemplate?.formFields ?? [],
        });
      } else {
        payload = buildContractPayload({
          templateKey: selectedDocumentId,
          title: documentTitle,
          formData,
          fillingType: "standard",
          schema: currentFormFields,
        });
      }
    } catch (err) {
      setGenerationStatus("idle");
      setErrorModalMessage(err?.message || "Não foi possível montar o documento.");
      return;
    }

    payload.job_id = id;

    const result = await contractService.generateContract(payload);

    if (result.success) {
      setGenerationStatus("processing");
    } else {
      setGenerationStatus("idle");
      setErrorModalMessage(
        normalizeContractErrorMessage(result?.error)
      );
    }
  };

  if (generationStatus === "processing") {
    return (
      <>
        <Helmet>
          <title>Gerando Documento</title>
        </Helmet>

        <div className="flex min-h-[50vh] items-center justify-center px-4 py-16">
          <div className="text-center space-y-4 max-w-lg">
            <LoadingMark size="md" className="mx-auto" />
            <h2 className="text-lg sm:text-xl font-semibold">
              Seu documento está sendo gerado. Aguardando confirmação do PDF no servidor…
            </h2>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1">
              <span className="h-2 w-2 rounded-full bg-primary animate-ping" />
              <p
                key={processingMessageIndex}
                className="text-sm font-medium text-foreground transition-opacity duration-200"
              >
                {PROCESSING_PROGRESS_MESSAGES[processingMessageIndex]}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isLoadingDocumentForEdit) {
    const loadingFromTemplate = Boolean(searchParams.get("user_template_id"));
    return (
      <>
        <Helmet>
          <title>{loadingFromTemplate ? "Carregando Modelo" : "Carregando Documento"}</title>
        </Helmet>
        <div className="flex min-h-[50vh] items-center justify-center px-4 py-16">
          <LoadingMark
            size="md"
            message={
              loadingFromTemplate
                ? "Carregando modelo personalizado..."
                : "Carregando documento para edicao..."
            }
            messageClassName="text-lg sm:text-xl font-semibold text-foreground"
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isDemoMode
            ? "Conhecer o sistema"
            : isEditingExisting
              ? "Editar Documento"
              : "Gerar Documento"}
        </title>
      </Helmet>

      <div
        className={`flex flex-col overflow-hidden min-w-0 ${
          isDemoMode ? "h-full" : "h-dvh"
        }`}
      >
        <header
          className={`shrink-0 z-10 bg-card border-b border-border/80 px-4 py-4 sm:p-6 lg:bg-card/90 lg:backdrop-blur-sm ${
            isDemoMode ? "" : "pt-16 lg:pt-6"
          }`}
        >
          <div className="flex flex-col gap-3 min-w-0">
            <div className="flex flex-col gap-1 min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold truncate">
                {isDemoMode
                  ? "Conhecer o sistema"
                  : isEditingExisting
                    ? "Editar Documento"
                    : "Gerar Documento"}
              </h1>
              {isEditingExisting && !isDemoMode && (
                <p className="text-sm text-muted-foreground truncate">
                  Editando documento
                  {documentTitle ? ` (${documentTitle})` : ""}
                </p>
              )}
            </div>
            {isDemoMode && <DemoModeBanner />}
          </div>
        </header>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0 overflow-hidden">
          <div
            id="contract-generator-form-scroll"
            className="overflow-y-auto min-h-0 min-w-0 p-4 sm:p-6 space-y-6 lg:border-r lg:border-border/60"
          >
                {!isDemoMode ? (
                  <LicenseLimitBanner user={generatorUser} className="!mb-0" />
                ) : null}
                {useFormWizard && formStepsConfig && !isPersonalizedMode && (
                  <ContractFormStepper
                    steps={formStepsConfig}
                    activeIndex={formStepIndex}
                    onStepChange={setFormStepIndex}
                  />
                )}

                {loadedStatus !== "PDF_GENERATED" &&
                  (!useFormWizard || formStepIndex === 0) &&
                  !isReviewStep && (
                    <ContractTypeSelector
                      contractMethod={contractMethod}
                      onContractMethodChange={handleContractMethodChange}
                      selectedType={selectedDocumentId}
                      onTypeChange={handleDocumentChange}
                      contractTypes={availableContractTypes}
                      selectedUserTemplateId={selectedUserTemplateId}
                      onUserTemplateChange={handleUserTemplateChange}
                      userTemplateOptions={userTemplateOptions}
                      userTemplatesLoading={
                        userTemplatesLoading || userTemplateDetailLoading
                      }
                      disabled={isEditingExisting}
                      hideMethodToggle={isDemoMode}
                      hidePersonalizedHint={isDemoMode}
                    />
                  )}

                {!isDemoMode &&
                  !isPersonalizedMode &&
                  (!useFormWizard || formStepIndex === 0) &&
                  !isReviewStep && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-2 w-full sm:w-auto"
                        onClick={handleOpenImportModal}
                      >
                        <Icon name="FileDown" size={18} />
                        Importar dados
                      </Button>
                      {isLocalEnv &&
                        selectedDocumentId === "contrato-locacao-imovel" && (
                          <Button
                            type="button"
                            variant="secondary"
                            className="gap-2 w-full sm:w-auto"
                            onClick={handleFillContratoLocacaoTestData}
                          >
                            Preencher com teste
                          </Button>
                        )}
                      {isLocalEnv &&
                        selectedDocumentId ===
                          "autorizacao-locacao-imovel" && (
                          <Button
                            type="button"
                            variant="secondary"
                            className="gap-2 w-full sm:w-auto"
                            onClick={handleFillAutorizacaoLocacaoTestData}
                          >
                            Preencher com teste
                          </Button>
                        )}
                      {isLocalEnv &&
                        selectedDocumentId === "contrato-parceria-vendas" && (
                          <Button
                            type="button"
                            variant="secondary"
                            className="gap-2 w-full sm:w-auto"
                            onClick={handleFillParceriaVendasTestData}
                          >
                            Preencher com teste
                          </Button>
                        )}
                    </div>
                  )}

                {(!useFormWizard || formStepIndex === 0) && !isReviewStep && (
                  <Input
                    label="Título do Documento"
                    value={documentTitle}
                    required
                    onChange={(e) =>
                      setDocumentTitle(clampDocumentTitle(e.target.value))
                    }
                    maxLength={MAX_DOCUMENT_TITLE_LENGTH}
                  />
                )}

                {isReviewStep && !isPersonalizedMode && (
                  <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                    <h2 className="text-lg font-semibold">
                      Revisão antes de gerar
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Confira os dados abaixo. Para alterar algo, volte à etapa
                      correspondente ou clique no passo no topo.
                    </p>
                    <dl className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="text-muted-foreground">
                          Tipo de documento
                        </dt>
                        <dd className="font-medium text-foreground">
                          {definition.label}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Título</dt>
                        <dd className="font-medium text-foreground break-words">
                          {documentTitle?.trim() ? documentTitle : "—"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}

                {!isPersonalizedMode &&
                  (!useFormWizard ||
                    (formStepIndex > 0 && wizardFormFields.length > 0)) && (
                  <DynamicContractForm
                    fields={
                      useFormWizard ? wizardFormFields : currentFormFields
                    }
                    fullFormSchema={currentFormFields}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    onFieldFocus={handleFieldFocus}
                    onFieldBlur={handleFieldBlur}
                    customerImport={
                      isDemoMode ? undefined : definition?.customerImport
                    }
                    onAppendRepeatableRows={handleAppendRepeatableRows}
                    layoutMode={
                      useFormWizard
                        ? isReviewStep
                          ? "accordion"
                          : "stepper"
                        : "accordion"
                    }
                  />
                )}

                {isPersonalizedMode && !isReviewStep && (
                    <PersonalizedContractForm
                      fields={loadedPersonalTemplate?.formFields ?? []}
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      onFieldFocus={handleFieldFocus}
                      onFieldBlur={handleFieldBlur}
                    />
                  )}

                {useFormWizard && formStepsConfig && !isPersonalizedMode && (
                  <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={formStepIndex === 0}
                      onClick={() =>
                        setFormStepIndex((i) => Math.max(0, i - 1))
                      }
                      className="w-full sm:w-auto"
                    >
                      Anterior
                    </Button>
                    <Button
                      type="button"
                      disabled={
                        formStepIndex >= formStepsConfig.length - 1
                      }
                      onClick={() =>
                        setFormStepIndex((i) =>
                          Math.min(formStepsConfig.length - 1, i + 1)
                        )
                      }
                      className="w-full sm:w-auto"
                    >
                      Próximo
                    </Button>
                  </div>
                )}
          </div>

          <div
            ref={desktopPreviewScrollRef}
            className="hidden lg:block overflow-y-auto min-h-0 min-w-0 p-6 bg-muted/20"
          >
                {isPersonalizedMode ? (
                  <PersonalizedLiveContractPreview
                    documentContent={loadedPersonalTemplate?.content}
                    formData={formData}
                    formFields={loadedPersonalTemplate?.formFields ?? []}
                    scrollContainerRef={desktopPreviewScrollRef}
                    scrollToPlaceholder={scrollToPlaceholder}
                    highlightedPlaceholder={highlightedPlaceholder}
                    onScrollDone={() => setScrollToPlaceholder(null)}
                  />
                ) : (
                  <LiveContractPreview
                    template={currentDocument}
                    formData={formData}
                    userConfiguration={userConfiguration}
                    scrollContainerRef={desktopPreviewScrollRef}
                    scrollToPlaceholder={scrollToPlaceholder}
                    highlightedPlaceholder={highlightedPlaceholder}
                    onScrollDone={() => setScrollToPlaceholder(null)}
                  />
                )}
          </div>
        </div>

        <GenerateContractButton
          isFormValid={isFormValid}
          generationStatus={generationStatus}
          requiredFieldsCount={requiredFieldsCount}
          filledFieldsCount={filledRequiredFields}
          onGenerate={
            isDemoMode ? handleDemoGenerateGate : handleGenerateContract
          }
          onSaveDraft={handleSaveDraft}
          onBackToTemplate={
            selectedUserTemplateId ? handleBackToTemplate : undefined
          }
          showSaveDraft={
            !isDemoMode &&
            (!isEditingExisting ||
              loadedStatus === "DRAFT" ||
              loadedStatus === "PDF_GENERATED")
          }
          bypassConfirmModal={isDemoMode}
          generateButtonLabel={
            isDemoMode ? "Gerar PDF" : "Gerar Documento PDF"
          }
          readyStatusLabel={
            isDemoMode ? "Pronto para continuar" : "Pronto para gerar"
          }
        />

        {!isDemoMode && (
          <DocumentFillerChatPanel
            formData={formData}
            setFormData={setFormData}
            templateKey={documentFillerTemplateKey}
            formSchema={currentFormFields}
            neoCoins={neoCoins}
            onNeoCoinBalanceUpdate={setNeoCoins}
          />
        )}

        {isDemoMode && (
          <DemoSignupGateModal
            open={signupGateOpen}
            onClose={() => setSignupGateOpen(false)}
            onContinueToSignup={handleContinueToSignup}
          />
        )}

            {/* Floating Preview Button (Mobile) */}
            <button
              type="button"
              onClick={() =>
                setMobilePreviewOpen((v) => !v)
              }
              className="
                lg:hidden
                fixed
                bottom-24
                right-5
                z-40
                flex
                items-center
                gap-2
                px-4
                py-3
                rounded-full
                bg-primary
                text-white
                shadow-lg
                hover:scale-105
                transition
              "
            >
              {mobilePreviewOpen ? "Fechar Prévia" : "Ver Prévia"}
            </button>

            {mobilePreviewOpen && (
              <div
                className="lg:hidden fixed inset-0 z-50 flex flex-col bg-background"
                role="dialog"
                aria-modal="true"
                aria-label="Prévia do documento"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
                  <h2 className="text-lg font-semibold">
                    Prévia do Documento
                  </h2>

                  <button
                    onClick={() => setMobilePreviewOpen(false)}
                    className="p-2 rounded-md hover:bg-muted"
                  >
                    ✕
                  </button>
                </div>

                <div
                  ref={mobilePreviewScrollRef}
                  className="flex-1 overflow-y-auto p-4 bg-muted/20"
                >
                  {isPersonalizedMode ? (
                    <PersonalizedLiveContractPreview
                      documentContent={loadedPersonalTemplate?.content}
                      formData={formData}
                      formFields={loadedPersonalTemplate?.formFields ?? []}
                      scrollContainerRef={mobilePreviewScrollRef}
                      scrollToPlaceholder={scrollToPlaceholder}
                      highlightedPlaceholder={highlightedPlaceholder}
                      onScrollDone={() => setScrollToPlaceholder(null)}
                    />
                  ) : (
                    <LiveContractPreview
                      template={currentDocument}
                      formData={formData}
                      userConfiguration={userConfiguration}
                      scrollContainerRef={mobilePreviewScrollRef}
                      scrollToPlaceholder={scrollToPlaceholder}
                      highlightedPlaceholder={highlightedPlaceholder}
                      onScrollDone={() => setScrollToPlaceholder(null)}
                    />
                  )}
                </div>
              </div>
            )}

            <ImportDocumentDataModal
              isOpen={importModalOpen}
              onClose={() => setImportModalOpen(false)}
              templateKey={toBackendTemplateKey(selectedDocumentId)}
              onConfirmImport={handleImportDocumentData}
            />

            {errorModalMessage && (
              <ModalShell
                backdropPadding="all"
                zClass={MODAL_LAYER.standard}
                backdropClassName="bg-black/50"
                panelClassName="w-full max-w-md bg-card border border-error/30 rounded-lg shadow-elevation-sm p-6 mx-4"
              >
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label="Erro ao gerar documento"
                >
                  <h2 className="text-base font-semibold text-foreground mb-2">
                    Erro
                  </h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {errorModalMessage}
                  </p>
                  <div className="mt-6 flex justify-end">
                    <Button
                      variant="destructive"
                      onClick={() => setErrorModalMessage(null)}
                    >
                      OK
                    </Button>
                  </div>
                </div>
              </ModalShell>
            )}
      </div>
    </>
  );
};

export default ContractGenerator;